import { Add, ArrowBack, Done, Edit, History } from '@mui/icons-material';
import { Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from 'aws-amplify/api';
import { ImagesDashboard, ImagesDashboardRef } from 'components/HelpEditor/Images/ImagesDashboard';
import { useToaster } from 'contexts/ToasterContext';
import React, { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as SourceService from 'services/sourceService';
import type { EditorViewRef } from '../components/HelpEditor/EditorView';
const EditorViewLazy = React.lazy(() =>
  import('../components/HelpEditor/EditorView').then((module) => ({
    default: module.EditorView,
  }))
);
import { HelpPreview } from '../components/HelpEditor/HelpPreview';
import { ROUTE_KEY } from '../constants/RouteConstants';
import { ActiveMenuContext } from '../contexts/ActiveMenuContext';
import { HelpContentProvider, useHelpContent } from '../contexts/HelpContentContext';
import { ImagesProvider } from '../contexts/ImagesContext';
import { useActionAccess } from '../hooks/useActionAccess';

export const HelpPage = () => {
  const { setActiveMenu } = useContext(ActiveMenuContext);
  const { state } = useLocation();
  const activeHeader = state?.activeHeader ?? 'Help';

  useEffect(() => {
    setActiveMenu(ROUTE_KEY.HELP);
  }, []);

  return (
    <HelpContentProvider>
      <HelpPageContent activeHeader={activeHeader} />
    </HelpContentProvider>
  );
};

const HelpPageContent = ({ activeHeader }: { activeHeader: string }) => {
  const { canEditHelpPage } = useActionAccess();
  const { triggerToast } = useToaster();
  const { fetchContent, initalContent, updateContent } = useHelpContent();
  const [activeTab, setActiveTab] = useState(activeHeader);
  const [editDocumentView, setEditDocumentView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const editorRef = React.useRef<EditorViewRef>(null);
  const imagesDashboardRef = useRef<ImagesDashboardRef>(null);
  const { mutate: restoreContent, isLoading: isRestoring } = useMutation(
    SourceService.restoreFaqsContent,
    {
      onError(error: ApiError) {
        const msg = error?.message ?? 'Failed to restore document';
        triggerToast({
          message: msg,
          type: 'error',
        });
      },
      onSuccess() {
        fetchContent().then(() => {
          setEditDocumentView(false);
          triggerToast({
            message: 'Document successfully restored',
            type: 'success',
          });
        });
      },
    }
  );

  const handleSaveDocument = () => {
    if (editorRef.current) {
      editorRef.current.saveDocument();
    }
  };

  const handleBackFromEditDocument = () => {
    updateContent(initalContent);
    setEditDocumentView(false);
  };

  const RenderTab = () => (
    <Stack direction="row" justifyContent={'space-between'} alignItems={'flex-start'}>
      <Stack direction="row" alignItems="center">
        <Typography
          variant="h5"
          sx={{
            cursor: `${canEditHelpPage ?'pointer':''}`,
            fontWeight: 'bold',
            position: 'relative',
            textTransform: 'uppercase',
            borderBottom: `5px solid ${activeTab === 'Help' ? '#f86d20' : '#999999'}`,
          }}
          onClick={() => setActiveTab('Help')}
        >
          Help &nbsp;&nbsp;
        </Typography>
        {canEditHelpPage && (
          <Typography
            variant="h5"
            sx={{
              cursor: 'pointer',
              fontWeight: 'bold',
              position: 'relative',
              textTransform: 'uppercase',
              borderBottom: `5px solid ${activeTab === 'Images' ? '#f86d20' : '#999999'}`,
            }}
            onClick={() => setActiveTab('Images')}
          >
            Images &nbsp;
          </Typography>
        )}
      </Stack>
      {activeTab === 'Help' && (
        <Stack direction={{ md: 'row', sm: 'column' }} alignItems="center" spacing={1}>
          {canEditHelpPage && (
            <>
              {editDocumentView ? (
                <>
                  <Button
                    onClick={() => handleBackFromEditDocument()}
                    size="small"
                    variant="contained"
                    startIcon={<ArrowBack />}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Back
                  </Button>

                  <Button
                    onClick={() => restoreContent()}
                    size="small"
                    variant="contained"
                    endIcon={<History />}
                    sx={{ fontWeight: 'bold' }}
                    disabled={isRestoring}
                  >
                    Restore Document
                  </Button>

                  <Button
                    onClick={() => handleSaveDocument()}
                    size="small"
                    variant="contained"
                    endIcon={<Done />}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Save Document
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setEditDocumentView(true)}
                    size="small"
                    variant="contained"
                    startIcon={<Edit />}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Edit Document
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      )}
      {activeTab == 'Images' && canEditHelpPage && (
        <Stack direction={{ md: 'row', sm: 'column' }} alignItems="center" spacing={1}>
          <Button
            onClick={() => {
              if (imagesDashboardRef.current) {
                imagesDashboardRef.current.openAddImagePopup();
              }
            }}
            size="small"
            variant="contained"
            startIcon={<Add />}
            sx={{ fontWeight: 'bold' }}
          >
            Add New Image
          </Button>
        </Stack>
      )}
    </Stack>
  );

  const renderCurrentView = () =>
    activeTab === 'Help' ? (
      !editDocumentView ? (
        <HelpPreview />
      ) : (
        <Suspense
          fallback={
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 4,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <EditorViewLazy
            ref={editorRef}
            setIsLoading={setIsLoading}
            setEditDocumentView={setEditDocumentView}
          />
        </Suspense>
      )
    ) : (
      <ImagesProvider>
        <ImagesDashboard ref={imagesDashboardRef} />
      </ImagesProvider>
    );

  return (
    <Container
      maxWidth={false}
      sx={{ pt: `${!editDocumentView ? '25px' : '10px'}` }}
      disableGutters
    >
      {RenderTab()}
      <Box sx={{ width: '100%', mt: 1 }}>{renderCurrentView()}</Box>

      {(isLoading || isRestoring) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(158, 158, 158, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
            pointerEvents: 'auto', // intercept clicks
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Processing...
          </Typography>
        </Box>
      )}
    </Container>
  );
};
